import { FacebookLoginProvider, useWidget, VoteApiProvider } from '@telescope/cassini-hooks';
import { CassiniProvider, Container, extendTheme, Spinner, withDefaultColorScheme } from '@telescope/cassini-ui';
import { getAppConfig } from '@telescope/cassini-utilities';
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import TagManager from 'react-gtm-module';
import { BrowserRouter as Router } from 'react-router-dom';

import { FallbackError } from 'components';
import { AuthProvider } from 'features';
import { baseTheme } from 'theme';
import { deepMapKeys } from 'utils';
import { StorageProvider } from './StorageProvider';

const { appHash } = getAppConfig();

type AppProviderProps = {
  children: React.ReactNode;
};

const { basename } = getAppConfig();

export const AppProvider = ({ children }: AppProviderProps) => {
  const { data } = useWidget();
  const { settings: authSettings } = data.widget.globalViews.auth;
  const { googleAnalytics } = data.widget.settings.general;

  // Initialize Google Tag Manager
  useEffect(() => {
    if (googleAnalytics.isEnabled) {
      TagManager.initialize({
        gtmId: googleAnalytics.gtmContainerId as string,
        dataLayer: {
          client_ga_id: googleAnalytics.gaClientMeasurementId ?? '',
          product_ga_id: googleAnalytics.gaProductMeasurementId ?? '',
          snapshot_type: data.snapshot.type,
        },
      });
    }
  }, [
    data.snapshot.type,
    googleAnalytics.gaProductMeasurementId,
    googleAnalytics.gaClientMeasurementId,
    googleAnalytics.gtmContainerId,
    googleAnalytics.isEnabled,
  ]);

  // Campaign manager has a bug where it doesn't like objects with keys that are numbers, so color values need to be prefixed with an '_'.
  // Strip out the prefixed colors before extending the theme.
  const themeColors = deepMapKeys(data.widget.theme.colors, (key) => (key.startsWith('_') ? key.slice(1) : key));
  const semanticTokens = {
    colors: Object.values(data.widget.theme.semanticTokens).reduce((prev: any, cur: any) => ({ ...prev, ...cur }), {}),
  };
  const widgetTheme = { ...data.widget.theme, colors: themeColors, semanticTokens };

  const customTheme = extendTheme(
    baseTheme,
    widgetTheme,
    withDefaultColorScheme({ colorScheme: widgetTheme.settings.defaultColorScheme })
  );

  console.log(customTheme);

  return (
    <StorageProvider prefix={`${appHash}:`}>
      <CassiniProvider theme={customTheme}>
        <React.Suspense
          fallback={
            <Container centerContent>
              <Spinner />
            </Container>
          }>
          <ErrorBoundary FallbackComponent={FallbackError}>
            <Router basename={basename}>
              <FacebookLoginProvider
                persistLogin={false}
                appId={authSettings.fbAppId}
                storagePrefix={appHash}
                isLoginEnabled={authSettings.isFacebookLoginEnabled && !!authSettings.fbAppId}>
                <VoteApiProvider
                  settings={{
                    apiKey: data.snapshot.api_key,
                    versionId: `ssp_${data.snapshot.widget_id}_${data.snapshot.version_id}`,
                  }}>
                  <AuthProvider>{children}</AuthProvider>
                </VoteApiProvider>
              </FacebookLoginProvider>
            </Router>
          </ErrorBoundary>
        </React.Suspense>
      </CassiniProvider>
    </StorageProvider>
  );
};
